import {
    filter, get, includes, omit, replace,
} from 'lodash';

import {
    TextTemplateValues,
    TemplateTextDynamic,
    TemplateTextValueDisplayLabelMap,
    DefaultOpenTemplateTextChar,
    DefaultCloseTemplateTextChar,
    TemplateTextOptionsTE,
    TemplateTextOptionsEmployerRep,
    ActionsTemplateValues,
    TemplateActionsValueDisplayLabelMap,
    ToolbarOptionsTemplateTextValueLabelMap,
} from './TemplateTextConsts';
import {
    UserRoles,
    EMPLOYER_ROLE,
    TALENYA_ROLE,
} from '../../../../constants/UserRole';

const SUPPORTED_CAMPAIGN_SENDER_TYPES = [EMPLOYER_ROLE, TALENYA_ROLE];

const TE_ONLY_VALUES = [
    TextTemplateValues.recruiterName,
    TextTemplateValues.recruiterFirstName,
    TextTemplateValues.recruiterEmail,
    TextTemplateValues.recruiterPhone,
    TextTemplateValues.recruiterJobTitle,
];

const EMPLOYER_REP_ONLY_VALUES = [
    TextTemplateValues.employerRepName,
    TextTemplateValues.employerRepFirstName,
    TextTemplateValues.employerRepEmail,
    TemplateTextDynamic.employerRepPhone,
    TemplateTextDynamic.employerRepJobTitle,
    TemplateTextDynamic.employerRepLinkedin,
];

const contactCandidateTagNames = [
    TextTemplateValues.candidateName,
    TextTemplateValues.candidateFirstName,
    TextTemplateValues.candidateLastName,
    TextTemplateValues.employerCompanyName,
    TextTemplateValues.employerCompanyWebsite,
    TextTemplateValues.employerRepName,
    TextTemplateValues.employerRepEmail,
    TextTemplateValues.employerRepPhone,
    TextTemplateValues.employerRepJobTitle,
    TextTemplateValues.positionJobTitle,
    TextTemplateValues.positionLocation,
    TextTemplateValues.positionDescriptionLink,
    TextTemplateValues.recruiterName,
    TextTemplateValues.recruiterFirstName,
    TextTemplateValues.recruiterEmail,
    TextTemplateValues.recruiterPhone,
    TextTemplateValues.recruiterJobTitle,
];

const allTagNames = Object.values(
    omit(TextTemplateValues, ['iHaveAQuestion', 'iHaveAComment', 'atsJobUrl']),
);

export const convertTextTemplateForEditor = (initialSourceText, tagNames) => {
    const tagNamesToReplace = tagNames || allTagNames;
    const processFunctionList = tagNamesToReplace.map((tagName) => (source) => convertSpanToTextTemplate(source, tagName));
    return processFunctionList.reduce((result, processFunction) => processFunction(result), `<body>${initialSourceText} </body>`);
};

export const convertTextTemplateForSystem = (initialSourceText, tagNames) => {
    const tagNamesToReplace = tagNames || allTagNames;
    const processFunctionList = tagNamesToReplace.map((tagName) => (source) => convertTextTemplateToSpan(source, tagName));
    return processFunctionList.reduce((result, processFunction) => processFunction(result), initialSourceText);
};

export const convertSpanToTextTemplate = (sourceText, autoTextType) => {
    const regex = sourceText.match(`<span field="${autoTextType}"></span>`)
        ? new RegExp(`<span field="${autoTextType}"></span>`, 'g')
        : new RegExp(`<span field='${autoTextType}'></span>`, 'g');

    return replace(
        sourceText,
        regex,
        generateTemplateText(autoTextType),
    );
};

export const convertLinkToTextTemplate = (sourceText, autoTextType) => replace(
    sourceText,
    new RegExp(`<a href='${autoTextType}'></a>`, 'g'),
    `<a href='${autoTextType}'>${autoTextType}</a>`,
);

export const convertTextTemplateToSpan = (sourceText, autoTextType) => replace(
    sourceText,
    new RegExp(generateTemplateText(autoTextType), 'g'),
    `<span field='${autoTextType}'></span>`,
);

export const convertTextTemplateToLink = (sourceText, autoTextType) => replace(
    sourceText,
    new RegExp(`<a(.*?)href=('|")${autoTextType}('|")>${autoTextType}</a>`, 'g'),
    `<a href='${autoTextType}'></a>`,
);

export const getTemplateTextValue = (wrappedAutoText = '') => wrappedAutoText.slice(1, wrappedAutoText.length - 1);

export const getTemplateTextDisplayLabel = (wrappedAutoText) => {
    const textTemplateValue = getTemplateTextValue(wrappedAutoText);

    return TemplateTextValueDisplayLabelMap[textTemplateValue] || textTemplateValue;
};

export const getToolbarOptionsTemplateTextValueLabelMap = (value) => ToolbarOptionsTemplateTextValueLabelMap[value]
        || TemplateTextValueDisplayLabelMap[value]
        || value;

export const getTemplateActionDisplayLabel = (wrappedAutoText) => {
    const textTemplateValue = getTemplateTextValue(wrappedAutoText);

    return TemplateActionsValueDisplayLabelMap[textTemplateValue];
};

export const isValidTemplateTextValue = (templateTextValue) => {
    const validTextValues = Object.values(TextTemplateValues);

    return validTextValues.includes(templateTextValue);
};

export const isValidTemplateActionValue = (templateTextValue) => {
    const validTextValues = Object.values(ActionsTemplateValues);

    return validTextValues.includes(templateTextValue);
};

// Modified by AI on 05/14/2026. Edit #1.
const getAllowedOptions = (options, { isQuestionAndCommentEmailOptionsEnabled, isAtsJobUrlAvailable, campaignSenderType }) => {
    let resultOptions = options;
    const normalizedCampaignSenderType = includes(SUPPORTED_CAMPAIGN_SENDER_TYPES, campaignSenderType)
        ? campaignSenderType
        : null;

    const questionCommentOptions = [
        'CONTACT_CANDIDATE_EMAIL_FORM_BODY_AUTO_TEXT_EMPLOYER_I_HAVE_A_COMMENT',
        'CONTACT_CANDIDATE_EMAIL_FORM_BODY_AUTO_TEXT_EMPLOYER_I_HAVE_A_QUESTION',
    ];

    if (!isQuestionAndCommentEmailOptionsEnabled) {
        resultOptions = filter(resultOptions, ((option) => !includes(questionCommentOptions, option.label)));
    }

    if (!isAtsJobUrlAvailable) {
        resultOptions = filter(resultOptions, ((option) => option.label !== 'CONTACT_CANDIDATE_EMAIL_FORM_BODY_AUTO_TEXT_ATS_JOB_URL'));
    }

    if (normalizedCampaignSenderType === EMPLOYER_ROLE) {
        resultOptions = filter(resultOptions, (option) => !includes(TE_ONLY_VALUES, option.value));
    } else if (normalizedCampaignSenderType === TALENYA_ROLE) {
        resultOptions = filter(resultOptions, (option) => !includes(EMPLOYER_REP_ONLY_VALUES, option.value));
    }

    return resultOptions;
};

export const generateTemplateText = (sourceText) => `${DefaultOpenTemplateTextChar}${sourceText}${DefaultCloseTemplateTextChar}`;

export const getSearchValue = (sourceText = '') => sourceText.slice(1, sourceText.length);

export const getTemplateTextOptions = (user, config) => {
    const isEmployerRepUser = get(user, 'impersonation.realEntityRole') === UserRoles.employerRep;

    const userOptions = isEmployerRepUser ? TemplateTextOptionsEmployerRep : TemplateTextOptionsTE;
    return getAllowedOptions(userOptions, config);
};
